/**
 * Bidirectional case-transform interceptor (API requests only).
 *
 * The FastAPI backend serializes request and response bodies in snake_case
 * — that is the public wire format and other consumers depend on it. The
 * Angular app works in camelCase: types are generated from the OpenAPI
 * document via `scripts/openapi-camelize.ts` + `openapi-typescript`, so
 * compile-time contracts are already camelCase. This interceptor closes the
 * loop at runtime for every HTTP call:
 *
 *   - Outgoing requests:  camelCase body → snake_case body
 *   - Incoming responses: snake_case body → camelCase body
 *
 * Dictionary-with-user-controlled-keys are preserved as-is. Some payloads
 * (notably `form_config`) contain maps where the keys are runtime data —
 * indicator `technical_label`s such as `energy_consumption_total`. The
 * `MAP_KEY_NAMES` set marks the parent property names of those maps; when
 * we recurse through a direct object child of one of them we leave the
 * immediate child keys untouched but keep transforming everything deeper.
 *
 * Preservation is scoped to direct object children only — when the value
 * under a `MAP_KEY_NAMES` parent is an array (e.g. `indicators: [...]`
 * returning a list of `IndicatorRead`), the array elements are camelized
 * normally. Only object-shaped values (e.g. `indicators: { foo: ... }`
 * acting as a dict) trigger key preservation.
 *
 * Only requests targeting the backend (`environment.apiUrl`) are transformed:
 * snake_case is the API's wire format, not a convention of every JSON the app
 * fetches. Static assets loaded over HttpClient (e.g. the i18n translation
 * files, whose keys may legitimately contain underscores) must reach their
 * consumers verbatim.
 *
 * Non-JSON bodies (FormData, Blob, ArrayBuffer, URLSearchParams) are passed
 * through untouched. The multipart indicator batch is the notable case: its
 * JSON `operations` part is snake_cased by the caller (see
 * `IndicatorApi.saveBatch`) before the FormData reaches this
 * interceptor, not here — this interceptor only transforms plain-object bodies.
 */

import { HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { MAP_KEY_NAMES, transform } from '@shared/utils/transform';
import { Utils } from '@shared/utils/utils';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

const isPlainObjectBody = (body: unknown): boolean => {
  if (body === null || body === undefined) return false;
  if (Array.isArray(body)) return true;
  if (typeof body !== 'object') return false;
  if (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof URLSearchParams
  ) {
    return false;
  }

  //  true for objects created with {...}, new Object(), or JSON.parse('{...}')
  const proto: unknown = Object.getPrototypeOf(body);
  return proto === Object.prototype || proto === null;
};

export const caseTransformInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  if (!req.url.startsWith(environment.apiUrl)) {
    return next(req);
  }

  let outgoing = req;
  if (isPlainObjectBody(req.body)) {
    outgoing = req.clone({ body: transform(req.body, Utils.toSnake, MAP_KEY_NAMES) });
  }
  return next(outgoing).pipe(
    map((event) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      if (event.type !== 4 /* HttpEventType.Response */) return event;
      const response = event as { body: unknown; clone: (init: { body: unknown }) => unknown };
      if (!isPlainObjectBody(response.body)) return event;
      return response.clone({ body: transform(response.body, Utils.toCamel, MAP_KEY_NAMES) }) as typeof event;
    }),
  );
};
